<template>

  <!--
    DeliberationTemplate.vue
    Reproduction fidèle du document :
    "Deliberation Licence1_BD_jan25 - S1 - premiere.pdf"
    Tableau de délibération - DAKAR INSTITUTE OF TECHNOLOGY
    Format : A4 paysage (landscape). Toutes les données sont injectables via props.
  -->
  <div
    class="delib-page tableau-delib-root document-container"
    :class="footerCompactClass"
    :style="pageStyle"
  >
    <div class="delib-body">
    <div class="delib-top">
    <header class="delib-header">
      <div class="logo">
        <!-- Image dynamique : passez l'URL via la prop "logoUrl".
             Tant qu'aucune image n'est fournie, un emplacement réservé s'affiche. -->
        <img v-if="logoUrl" :src="logoUrl" alt="Logo de l'institut" class="logo-img" />
        <div v-else class="logo-placeholder">
          <span class="ph-icon">&#128444;</span>
          <span>Logo de l'institut</span>
        </div>
      </div>
      <div class="title-box">
        <div class="title-1">{{ header.title }}</div>
        <div class="title-2">{{ header.subtitle }}</div>
      </div>
    </header>

    <!-- ============ BLOCS UE (résumé des EC) ============ -->
    <section class="ue-blocks delib-ue-blocks-section" :style="ueBlocksGridStyle">
      <div class="ue-block" v-for="(ue, i) in ueBlocks" :key="i">
        <div class="ue-block-title">{{ ue.title }}</div>
        <table class="ue-block-table">
          <colgroup>
            <col class="ue-ec-col" />
            <col class="ue-coeff-col" />
            <col class="ue-moy-col" />
          </colgroup>
          <thead>
            <tr>
              <th class="left">Nom de l'EC</th>
              <th class="ue-num-col">Coeff</th>
              <th class="ue-num-col">Moy.</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(ec, j) in ue.ecs" :key="j">
              <td class="left">{{ ec.nom }}</td>
              <td class="ue-num-col">{{ ec.coeff }}</td>
              <td class="ue-num-col">{{ ec.moy }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
    </div>

    <!-- ============ TABLEAU PRINCIPAL ============ -->
    <div class="main-table-wrap">
    <table class="main-table tableau-deliberation-table" :style="tableStyle">
      <colgroup>
        <col class="col-mat" />
        <col class="col-nom" />
        <col class="col-prenom" />
        <col class="col-date" />
        <template v-for="(ue, i) in resolvedUeColumns" :key="'cg-' + i">
          <col class="col-ue" :style="ueColStyle" />
          <col class="col-ue" :style="ueColStyle" />
        </template>
        <col class="col-res" />
        <col class="col-res" />
        <col class="col-appr" />
      </colgroup>
      <thead>
        <tr class="group-row">
          <th colspan="4" class="grp grp-id">Identification de l'étudiant</th>
          <th
            v-for="(ue, i) in resolvedUeColumns"
            :key="'gh-' + i"
            colspan="2"
            class="grp grp-ue"
          >{{ ue.label || ue.code }}</th>
          <th colspan="3" class="grp grp-results">Résultats</th>
        </tr>
        <tr class="sub-row">
          <th class="sub-id">Matricule</th>
          <th class="sub-id left">NOM</th>
          <th class="sub-id left">Prénom</th>
          <th class="sub-id">Date de Naiss.</th>
          <template v-for="(ue, i) in resolvedUeColumns" :key="'sh-' + i">
            <th class="sub-ue">Moyenne</th>
            <th class="sub-ue">Validation</th>
          </template>
          <th class="sub-res">Tot. Crédits</th>
          <th class="sub-res">Moyenne</th>
          <th class="sub-res">Appréciation</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(s, si) in students" :key="s.matricule || s.inscription_id || si">
          <td class="matricule">{{ s.matricule }}</td>
          <td class="left upper nom">{{ s.nom }}</td>
          <td class="left prenom">{{ s.prenom }}</td>
          <td class="left date-naiss">{{ s.naissance }}</td>
          <template v-for="(u, ui) in normalizedStudentUes(s)" :key="'su-' + si + '-' + ui">
            <td>{{ u.moyenne }}</td>
            <td>{{ u.validation }}</td>
          </template>
          <td>{{ s.totalCredits }}</td>
          <td>{{ s.moyenneGenerale }}</td>
          <td>{{ s.appreciation }}</td>
        </tr>
      </tbody>
    </table>
    </div>

    <div class="delib-footer-section">
    <!-- Récap + signatures : bloc ancré en bas (espace flexible au-dessus du tableau) -->
    <div class="delib-bottom-anchor">
    <section class="footer-grid">
      <table class="recap-appr">
        <colgroup>
          <col class="appr-col-label" />
          <col class="appr-col-num" />
          <col class="appr-col-pct" />
        </colgroup>
        <thead>
          <tr><th class="left">Appréciation</th><th>Nombre</th><th>Pourcentage</th></tr>
        </thead>
        <tbody>
          <tr v-for="(a, i) in appreciationStats" :key="i">
            <td class="left">{{ a.label }}</td>
            <td>{{ a.nombre }}</td>
            <td>{{ a.pourcentage }}</td>
          </tr>
        </tbody>
      </table>

      <div class="recap-center">
        <div class="recap-ue-row">
          <table
            v-for="(u, i) in ueValidation"
            :key="i"
            class="recap-ue-single"
          >
            <colgroup>
              <col class="ue-label-col" />
              <col class="ue-val-col" />
            </colgroup>
            <thead>
              <tr><th colspan="2">{{ u.code || ('UE' + (i + 1)) }}</th></tr>
            </thead>
            <tbody>
              <tr><td class="left">Validé</td><td>{{ u.valide }}</td></tr>
              <tr><td class="left">Non Validé</td><td>{{ u.nonValide }}</td></tr>
              <tr><td class="left">Tx Réussite</td><td>{{ u.txReussite }}</td></tr>
            </tbody>
          </table>
        </div>

        <table class="recap-effectif">
          <thead>
            <tr><th>Effectif Total</th><th>Réussites</th><th>Taux de réussite</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>{{ effectif.total }}</td>
              <td>{{ effectif.reussites }}</td>
              <td>{{ effectif.taux }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="recap-jury">
        <div class="lieu-date">{{ lieuDate }}</div>
        <div class="jury-box">
          <div class="jury-label">Président du Jury :</div>
          <div class="jury-name" :class="{ 'jury-name--empty': !presidentJury }">{{ presidentJury || '\u00a0' }}</div>
        </div>
      </div>
    </section>

    <div class="foot-spacer" aria-hidden="true" />

    <table class="signatures">
      <tbody>
        <tr>
          <td rowspan="2" class="sig-directeur">
            <div v-if="signatureDirecteur.line1" class="sig-line1">{{ signatureDirecteur.line1 }}</div>
            <div v-if="signatureDirecteur.line2" class="sig-line2">{{ signatureDirecteur.line2 }}</div>
          </td>
          <td v-for="(sig, i) in signatureRow1" :key="'r1' + i">
            <div v-if="sig.line1" class="sig-line1">{{ sig.line1 }}</div>
            <div v-if="sig.line2" class="sig-line2" :class="{ italic: sig.line2Italic }">{{ sig.line2 }}</div>
          </td>
        </tr>
        <tr>
          <td v-for="(sig, i) in signatureRow2" :key="'r2' + i">
            <div v-if="sig.line1" class="sig-line1">{{ sig.line1 }}</div>
            <div v-if="sig.line2" class="sig-line2" :class="{ italic: sig.line2Italic }">{{ sig.line2 }}</div>
          </td>
        </tr>
      </tbody>
    </table>
    </div>
    </div>
    </div>

    <footer class="doc-footer">
      <span class="doc-footer-ref">{{ footerRef }}</span>
      <span class="doc-footer-page">{{ pageLabel }}</span>
    </footer>
  </div>

</template>

<script setup>
import { computed } from 'vue'
import {
  defaultHeader,
  defaultUeBlocks,
  defaultUeColumns,
  defaultStudents,
  defaultAppreciationStats,
  defaultUeValidation,
  defaultEffectif,
  defaultSignatureDirecteur,
  defaultSignatureMembers,
} from './deliberationTemplateDefaults'

const MEMBERS_ROW1_COUNT = 7

const props = defineProps({
  logoUrl:       { type: String,  default: '/images/logo-bulletin.jpg' },
  header:        { type: Object,  default: () => defaultHeader },
  ueColumns:     { type: Array,   default: () => defaultUeColumns },
  ueBlocks:      { type: Array,   default: () => defaultUeBlocks },
  students:      { type: Array,   default: () => defaultStudents },
  appreciationStats: { type: Array, default: () => defaultAppreciationStats },
  ueValidation:  { type: Array,   default: () => defaultUeValidation },
  effectif:      { type: Object,  default: () => defaultEffectif },
  lieuDate:      { type: String,  default: 'Fait à Dakar, le 31 Juillet 2025.' },
  presidentJury: { type: String,  default: 'Seydou Nourou SYLLA' },
  signatureDirecteur: { type: Object, default: () => defaultSignatureDirecteur },
  signatureMembers:   { type: Array,  default: () => defaultSignatureMembers },
  footerRef:     { type: String,  default: 'Delib. Licence1_BD_jan25-S1-premiere' },
  pageLabel:     { type: String,  default: '' },
  tableFontScale: { type: Number, default: 1 },
  ueCount:       { type: Number,  default: 0 },
  /** -1 = auto selon effectif ; 0 = normal ; 1–2 = récap/signatures plus serrés */
  footerCompact: { type: Number, default: -1 },
})

const resolvedUeColumns = computed(() => {
  if (props.ueColumns?.length) return props.ueColumns
  const n = props.ueCount || props.ueBlocks?.length || 4
  return Array.from({ length: n }, (_, i) => ({ code: `UE${i + 1}`, label: `UE${i + 1}` }))
})

const effectiveUeCount = computed(() => resolvedUeColumns.value.length)

const ueBlocksGridStyle = computed(() => ({
  gridTemplateColumns: `repeat(${Math.max(1, props.ueBlocks?.length || effectiveUeCount.value)}, 1fr)`,
}))

const pageStyle = computed(() => ({
  '--table-font-scale': String(props.tableFontScale || 1),
  '--ue-count': String(effectiveUeCount.value),
}))

const tableStyle = computed(() => {
  const n = effectiveUeCount.value
  const baseUeMm = n <= 4 ? 15 : n <= 5 ? 13 : n <= 6 ? 11 : 9
  return { '--col-ue-width': `${baseUeMm}mm` }
})

const ueColStyle = computed(() => ({
  width: 'var(--col-ue-width, 15mm)',
}))

function normalizedStudentUes(student) {
  const cols = effectiveUeCount.value
  const ues = student?.ues || []
  const out = []
  for (let i = 0; i < cols; i++) {
    out.push(ues[i] || { moyenne: '', validation: '' })
  }
  return out
}

const signatureRow1 = computed(() =>
  props.signatureMembers.slice(0, MEMBERS_ROW1_COUNT),
)
const signatureRow2 = computed(() =>
  props.signatureMembers.slice(MEMBERS_ROW1_COUNT),
)

const effectiveFooterCompact = computed(() => {
  if (props.footerCompact >= 0) return props.footerCompact
  const n = props.students?.length ?? 0
  if (n >= 28) return 2
  if (n >= 26) return 1
  return 0
})

const footerCompactClass = computed(() => ({
  'footer-compact-1': effectiveFooterCompact.value >= 1,
  'footer-compact-2': effectiveFooterCompact.value >= 2,
}))
</script>

<style scoped>

/* ====== Tokens — palette PDF (2 tons en-tête tableau) ====== */
.delib-page {
  --c-title-bg: #D9EAF7;
  --c-header-top: #D9EAF7;
  --c-header-sub: #E4DFEC;
  --c-foot-font: 6.5pt;
  --c-foot-font-lg: 7pt;
  --c-stripe: #EBF1DE;
  --c-border: #000;
  --c-text: #000000;

  width: 297mm;
  height: 210mm;
  box-sizing: border-box;
  position: relative;
  padding: 6mm 6mm 8mm;
  margin: 0 auto;
  background: #fff;
  color: var(--c-text);
  font-family: Arial, Helvetica, sans-serif;
  font-size: 6.5pt;
  font-weight: 400;
  line-height: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  -webkit-font-smoothing: auto;
  -moz-osx-font-smoothing: auto;
  text-rendering: geometricPrecision;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.delib-body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  /* Zone réservée pour la ligne « Delib. … » (position absolue, hors flux) */
  padding-bottom: 6mm;
  box-sizing: border-box;
}

.delib-top { flex: 0 0 auto; margin-bottom: 2mm; }

.delib-footer-section {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  font-size: var(--c-foot-font);
}

.delib-bottom-anchor {
  margin-top: auto;
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
}

/* ====== En-tête ====== */
.delib-header {
  display: flex;
  align-items: stretch;
  margin-bottom: 2.5mm;
}
.logo {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44mm;
  flex: 0 0 44mm;
  padding: 2mm 1.5mm 2mm 0;
}
.logo-img { max-height: 14mm; max-width: 42mm; object-fit: contain; }
.logo-placeholder {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  width: 42mm; height: 14mm; border: 1px dashed #9aa3ad; background: #f5f6f7;
  color: #8a8f96; font-size: 6pt; text-align: center;
}
.title-box {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: var(--c-title-bg);
  padding: 3mm 6mm;
  border: 1px solid var(--c-border);
}
.title-1 {
  font-size: 12pt;
  font-weight: 700;
  color: var(--c-text);
  text-align: center;
  line-height: 1.15;
}
.title-2 {
  font-size: 10.5pt;
  font-weight: 700;
  color: var(--c-text);
  text-align: center;
  line-height: 1.15;
  margin-top: 0.5mm;
}

/* ====== Blocs UE — colonnes sans bordures (comme le PDF) ====== */
.ue-blocks {
  display: grid;
  gap: 0;
}
.ue-block { min-width: 0; padding: 0 1mm; }
.ue-block + .ue-block { border-left: none; }
.ue-block-title {
  background: var(--c-title-bg);
  font-weight: 700;
  color: var(--c-text);
  text-align: center;
  padding: 0.8mm 1mm;
  font-size: 6.5pt;
  line-height: 1.15;
  margin-bottom: 0.6mm;
}
.ue-block-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
.ue-coeff-col { width: 6mm; }
.ue-moy-col { width: 9mm; }
.ue-block-table th,
.ue-block-table td {
  border: none;
  font-weight: 400;
  color: var(--c-text);
  padding: 0;
}
.ue-block-table th {
  font-weight: 700;
  text-align: center;
  font-size: 6pt;
  line-height: 1.15;
  background: #fff;
  vertical-align: bottom;
}
.ue-block-table th.left { text-align: left; }
.ue-block-table td.left {
  text-align: left;
  padding-right: 1px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.ue-block-table .ue-num-col {
  text-align: center;
  white-space: nowrap;
  overflow: visible;
  padding: 0;
}
.ue-block-table td {
  font-size: 6pt;
  line-height: 1.15;
  vertical-align: middle;
}

/* ====== Tableau principal ====== */
.main-table-wrap {
  flex: 0 0 auto;
  margin-bottom: 2mm;
}
.main-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}
.col-mat { width: 13mm; }
.col-nom { width: 21mm; }
.col-prenom { width: 25mm; }
.col-date { width: 26mm; }
.col-ue { width: var(--col-ue-width, 15mm); }
.col-res { width: 14mm; }
.col-appr { width: 26mm; }

.main-table th,
.main-table td {
  border: 1px solid var(--c-border);
  padding: 0 1.5px;
  text-align: center;
  vertical-align: middle;
  font-size: calc(6.25pt * var(--table-font-scale, 1));
  line-height: 1.05;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 0;
  color: var(--c-text);
}
.main-table thead th {
  font-weight: 700;
  font-size: calc(6pt * var(--table-font-scale, 1));
  line-height: 1.05;
  padding: 0 1.5px;
  color: var(--c-text);
}
.group-row th {
  height: 3.7mm;
  background: var(--c-header-top);
}
.sub-row th {
  height: 3.7mm;
  background: var(--c-header-sub);
}
.main-table tbody td { height: 3.65mm; }

.main-table .left { text-align: left; }
.main-table .upper { text-transform: uppercase; }
.main-table .nom { font-weight: 400; font-size: 6pt; letter-spacing: -0.01em; }
.main-table .prenom,
.main-table .date-naiss { font-size: 6pt; letter-spacing: -0.01em; }
.main-table .matricule { font-size: 6pt; }

.main-table tbody tr:nth-child(odd) td { background: #fff; color: var(--c-text); }
.main-table tbody tr:nth-child(even) td { background: var(--c-stripe); color: var(--c-text); }

/* ====== Bas de page — police plus grande (comme le PDF) ====== */
.footer-grid {
  display: grid;
  grid-template-columns: auto 1fr 52mm;
  gap: 0 3mm;
  align-items: start;
  flex: 0 0 auto;
}
.recap-appr {
  border-collapse: collapse;
  table-layout: fixed;
  width: max-content;
}
.appr-col-label { width: 24mm; }
.appr-col-num { width: 11mm; }
.appr-col-pct { width: 15mm; }
.recap-appr th,
.recap-appr td {
  border: 1px solid var(--c-border);
  padding: 0 2px;
  text-align: center;
  font-size: 6pt;
  line-height: 1;
  height: 3.2mm;
  vertical-align: middle;
  box-sizing: border-box;
  color: var(--c-text);
  white-space: nowrap;
}
.recap-appr .left { text-align: left; padding-left: 2px; }
.recap-appr th {
  background: var(--c-title-bg);
  font-weight: 700;
  overflow: visible;
}

.recap-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5mm;
  justify-self: center;
  width: 100%;
}
.recap-ue-row {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: stretch;
  justify-content: center;
  gap: 2mm;
  width: 100%;
}
.recap-ue-single {
  border-collapse: collapse;
  table-layout: fixed;
  width: 38mm;
  flex: 0 0 38mm;
}
.ue-label-col { width: 28mm; }
.ue-val-col { width: 10mm; }
.recap-ue-single th,
.recap-ue-single td {
  border: 1px solid var(--c-border);
  padding: 0 2px;
  text-align: center;
  font-size: var(--c-foot-font);
  line-height: 1.15;
  height: 4mm;
  color: var(--c-text);
}
.recap-ue-single .left { text-align: left; }
.recap-ue-single th { background: var(--c-title-bg); font-weight: 700; }

.recap-effectif {
  border-collapse: collapse;
  table-layout: fixed;
  width: 84mm;
  margin: 0 auto;
}
.recap-effectif th, .recap-effectif td {
  border: 1px solid var(--c-border);
  padding: 0 4px;
  text-align: center;
  font-size: var(--c-foot-font);
  line-height: 1.15;
  height: 4mm;
  color: var(--c-text);
}
.recap-effectif th { background: var(--c-title-bg); font-weight: 700; }

.recap-jury {
  display: flex;
  flex-direction: column;
  gap: 1mm;
  align-items: stretch;
  width: 52mm;
  justify-self: end;
}
.lieu-date {
  font-size: var(--c-foot-font-lg);
  line-height: 1.15;
  text-align: right;
  width: 100%;
  white-space: nowrap;
  color: var(--c-text);
}
.jury-box {
  border: 1px solid var(--c-border);
  padding: 1.5mm 2mm;
  width: 50mm;
  height: 24mm;
  box-sizing: border-box;
  align-self: end;
}
.jury-label,
.jury-name { font-size: var(--c-foot-font); line-height: 1.15; color: var(--c-text); }
.jury-name { margin-top: 0.8mm; min-height: 4mm; }
.jury-name--empty { color: transparent; user-select: none; }

.foot-spacer {
  flex: 0 0 auto;
  min-height: 8mm;
  max-height: 14mm;
  height: 10mm;
}

/* Dernière page multi : récap/signatures toujours ancrés en bas (prioritaire sur compact) */
.delib-page.footer-anchor-bottom .delib-footer-section {
  flex: 1 1 auto;
  min-height: 0;
}
.delib-page.footer-anchor-bottom .delib-bottom-anchor {
  margin-top: auto;
}

/* Dernière page multi avec place : espacements normaux (comme page unique) */
.delib-page.footer-anchor-bottom:not(.footer-compact-1) .main-table-wrap {
  margin-bottom: 2mm;
}
.delib-page.footer-anchor-bottom:not(.footer-compact-1) .foot-spacer {
  height: 10mm;
  min-height: 8mm;
  max-height: 14mm;
}
.delib-page.footer-anchor-bottom:not(.footer-compact-1) .jury-box {
  height: 24mm;
  padding: 1.5mm 2mm;
}
.delib-page.footer-anchor-bottom:not(.footer-compact-1) .signatures td {
  height: 11mm;
  padding: 1mm 1mm;
  line-height: 1.2;
}
.delib-page.footer-anchor-bottom:not(.footer-compact-1) .recap-center {
  gap: 1.5mm;
}
.delib-page.footer-anchor-bottom:not(.footer-compact-1) .recap-ue-row {
  gap: 2mm;
}

/* Compact 1 : récap sous le tableau (page unique uniquement) */
.delib-page.footer-compact-1:not(.footer-anchor-bottom) .delib-footer-section {
  flex: 0 0 auto;
}
.delib-page.footer-compact-1:not(.footer-anchor-bottom) .delib-bottom-anchor {
  margin-top: 0;
}
.delib-page.footer-compact-1 .main-table-wrap {
  margin-bottom: 1mm;
}
.delib-page.footer-compact-1 .foot-spacer {
  height: 4mm;
  min-height: 2mm;
  max-height: 5mm;
}
.delib-page.footer-compact-1 .jury-box {
  height: 18mm;
  padding: 1mm 2mm;
}
.delib-page.footer-compact-1 .signatures td {
  height: 9mm;
  padding: 0.6mm 1mm;
}
.delib-page.footer-compact-1 .recap-center {
  gap: 0.8mm;
}
.delib-page.footer-compact-1 .recap-ue-row {
  gap: 1mm;
}

/* Compact 2 : mode serré (28–30 étudiants sur une page) */
.delib-page.footer-compact-2 .foot-spacer {
  height: 1mm;
  min-height: 0;
  max-height: 2mm;
}
.delib-page.footer-compact-2 .jury-box {
  height: 14mm;
  padding: 0.8mm 1.5mm;
}
.delib-page.footer-compact-2 .signatures td {
  height: 7.5mm;
  padding: 0.5mm 0.8mm;
  line-height: 1.1;
}
.delib-page.footer-compact-2 .recap-appr th,
.delib-page.footer-compact-2 .recap-appr td {
  height: 2.8mm;
  font-size: 5.5pt;
}
.delib-page.footer-compact-2 .recap-ue-single th,
.delib-page.footer-compact-2 .recap-ue-single td,
.delib-page.footer-compact-2 .recap-effectif th,
.delib-page.footer-compact-2 .recap-effectif td {
  height: 3.2mm;
}
.delib-page.footer-compact-2 .recap-center {
  gap: 0.5mm;
}
.delib-page.footer-compact-2 .recap-jury {
  gap: 0.5mm;
}
.delib-page.footer-compact-2 .main-table-wrap {
  margin-bottom: 0.5mm;
}

/* ====== Membres du jury — directeur + 7 profs (ligne 1), suite ligne 2 ====== */
.signatures {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  flex: 0 0 auto;
  margin-bottom: 0.5mm;
}
.signatures td {
  border: 1px solid var(--c-border);
  height: 11mm;
  width: 12.5%;
  vertical-align: top;
  padding: 1mm 1mm;
  font-size: var(--c-foot-font);
  line-height: 1.2;
  overflow: hidden;
  color: var(--c-text);
}
.signatures .sig-directeur {
  height: auto;
}
.sig-line1, .sig-line2 { font-weight: 400; color: var(--c-text); }
.italic { font-style: italic; }

.doc-footer {
  position: absolute;
  left: 6mm;
  right: 6mm;
  bottom: 2mm;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  width: auto;
  font-size: 6pt;
  color: var(--c-text);
  line-height: 1;
  margin: 0;
  padding: 0;
  z-index: 2;
}
.doc-footer-ref {
  flex: 1 1 auto;
  min-width: 0;
}
.doc-footer-page {
  flex: 0 0 auto;
  text-align: right;
  white-space: nowrap;
}

@media print {
  @page { size: A4 landscape; margin: 0; }
  .delib-page {
    width: 297mm;
    height: 210mm;
    margin: 0;
    padding: 6mm 6mm 8mm;
    overflow: hidden;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}

</style>
